import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, QueryStatus, StatusBadge } from "@cx/ui";
import type { PurchaseOrderFormOptions, PurchaseOrderListResult } from "@cx/contracts";
import { formatBRL, formatDateBR } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { useSession } from "../../session";

/** Suprimentos › Pedidos de compra (RF-SUP-03): filtros por texto, situação e período; recebido x total. */
export function PurchaseOrdersListPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [statusId, setStatusId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const term = q.trim();
  const canCreate = can(perms, "purchase_orders", "create");
  const options = useQuery({
    queryKey: ["purchase-orders", companyId, "form-options"],
    queryFn: () => api<PurchaseOrderFormOptions>("/purchase-orders/form-options"),
  });
  const list = useInfiniteQuery({
    queryKey: ["purchase-orders", companyId, "list", term, statusId, from, to],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<PurchaseOrderListResult>(
        `/purchase-orders?${new URLSearchParams({
          ...(term ? { q: term } : {}),
          ...(statusId ? { statusId } : {}),
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  const summary = list.data?.pages[0]?.summary;
  const create = () => navigate({ to: "/suprimentos/pedidos-de-compra/novo" });

  return (
    <>
      <ContentHeader
        title="Pedidos de compra"
        breadcrumb={["Suprimentos", "Pedidos de compra"]}
        actions={
          canCreate && (
            <button type="button" className="btn btn-primary" onClick={create}>
              Incluir pedido
            </button>
          )
        }
      />
      <div className="box card">
        <div className="row g-2 p-3 pb-0">
          <div className="col-md-4">
            <label className="visually-hidden" htmlFor="po-q">
              Pesquisar pedido de compra
            </label>
            <input
              id="po-q"
              type="search"
              className="form-control"
              placeholder="Número, ordem de compra ou fornecedor"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          {options.data && (
            <div className="col-md-3">
              <label className="visually-hidden" htmlFor="po-status">
                Situação
              </label>
              <select
                id="po-status"
                className="form-select"
                value={statusId}
                onChange={(e) => setStatusId(e.target.value)}
              >
                <option value="">Todas as situações</option>
                {options.data.statuses.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="col-6 col-md-2">
            <label className="visually-hidden" htmlFor="po-from">
              Data inicial
            </label>
            <input
              id="po-from"
              type="date"
              className="form-control"
              title="Data inicial"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="col-6 col-md-2">
            <label className="visually-hidden" htmlFor="po-to">
              Data final
            </label>
            <input
              id="po-to"
              type="date"
              className="form-control"
              title="Data final"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
        </div>
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && rows.length === 0 && (
          <EmptyState
            title="Nenhum pedido de compra"
            description="Registre o que pediu ao fornecedor e receba em uma ou mais entregas: cada recebimento lança o estoque com custo e as contas a pagar."
            actions={canCreate ? [{ label: "Incluir pedido", onClick: create }] : []}
          />
        )}
        {rows.length > 0 && (
          <>
            <div className="table-responsive">
              <table className="table align-middle mb-0" aria-label="Pedidos de compra">
                <thead>
                  <tr>
                    <th scope="col">Número</th>
                    <th scope="col">Data</th>
                    <th scope="col">Previsto</th>
                    <th scope="col">Fornecedor</th>
                    <th scope="col">Ordem de compra</th>
                    <th scope="col" className="text-end">
                      Total
                    </th>
                    <th scope="col" className="text-end">
                      Recebido
                    </th>
                    <th scope="col">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <Link to="/suprimentos/pedidos-de-compra/$id" params={{ id: r.id }}>
                          {r.number}
                        </Link>
                      </td>
                      <td>{formatDateBR(r.date)}</td>
                      <td>{r.expectedDate ? formatDateBR(r.expectedDate) : "—"}</td>
                      <td>{r.supplierName}</td>
                      <td>{r.purchaseRef ?? "—"}</td>
                      <td className="text-end">{formatBRL(r.totalCents)}</td>
                      <td className="text-end">{formatBRL(r.receivedCents)}</td>
                      <td>
                        <StatusBadge tone={r.status.color}>{r.status.label}</StatusBadge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 p-3">
              {summary && summary.count >= 0 && (
                <span className="text-muted small" data-testid="po-summary">
                  {summary.count} {summary.count === 1 ? "pedido" : "pedidos"}
                  {summary.totalCents !== null && ` · total ${formatBRL(summary.totalCents)}`}
                </span>
              )}
              {list.hasNextPage && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={list.isFetchingNextPage}
                  onClick={() => list.fetchNextPage()}
                >
                  Carregar mais
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
